import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, Handshake, Info, Send } from 'lucide-react';
import { ROLE_NAME, errorMessage, useLocale, type MessageKey } from '../../i18n';
import { ConfirmButton } from '../ui/ConfirmButton';
import { ClubPicker } from '../auth/ClubPicker';
import {
  acceptOrganizationLink,
  endOrganizationLink,
  fetchOrganizationLinks,
  proposeOrganizationLink,
  updateOrganizationLink,
  type LinkRoleOption,
  type LinkRoleRef,
  type LinkWriteResult,
  type LinksListResult,
  type OrganizationLinkView,
} from '../../lib/api/organizationLinks';

// ─────────────────────────────────────────────────────────────────────
// SAMARBEID — the organization's own co-operation page (slice 4a, 2026-10-20).
//
// Until now only Starpoint made links, in /sa. Here a centre and a club enter
// a co-operation themselves, under the owner's rule of 2026-10-18 (decision 5)
// that the SERVER enforces — the page draws it so nobody discovers a refusal
// on use, but decides nothing:
//
//   • the CENTRE proposes, and chooses what the club gets at its courts;
//   • the CLUB says yes or no, and chooses what the centre gets in its
//     activities;
//   • afterwards each side changes only what IT gives; the agreed price is
//     the centre's;
//   • either side ends it, or withdraws / declines a proposal.
//
// A LINK GRANTS NOTHING YET, in practice. The evaluator reads active links since
// slice 4b, but no screen or route uses what a link gives before 4c, and
// the page says so once, up front, rather than letting somebody believe an
// agreement already opened the courts.
//
// Every string is a catalog key. A role's capabilities are said in words
// (`links.capability.*`), never as identifiers.
// ─────────────────────────────────────────────────────────────────────

export interface OrganizationLinksViewProps {
  /** The organization the portal is acting in — for the heading only. */
  organizationName: string | null;
  /** Back to the settings this page was opened from. */
  onBack: () => void;
  /** Injectable for tests. */
  load?: typeof fetchOrganizationLinks;
  propose?: typeof proposeOrganizationLink;
  accept?: typeof acceptOrganizationLink;
  update?: typeof updateOrganizationLink;
  end?: typeof endOrganizationLink;
}

type Notice = { kind: 'ok' | 'error'; text: string } | null;

/** The select's value for «give nothing». */
const NONE = '';

/**
 * A role's name for a person: the catalog's for a system role (Partnerklubb,
 * Vertssenter — in the reader's language), the name /sa gave a custom one.
 * The same map «Tilganger» and «Min konto» use.
 */
function useRoleName() {
  const { t } = useLocale();
  return (role: { key: string; name: string }): string => {
    const key = ROLE_NAME[role.key];
    return key ? t(key) : role.name;
  };
}

/** «30» → 30 days; '' → null (the centre's ordinary window); anything else → undefined. */
function parseWindow(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  return n >= 1 && n <= 365 ? n : undefined;
}

/** «300» → 30000 øre; '' → null (the list price); anything else → undefined (invalid). */
function parseRate(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed) * 100;
}

export function OrganizationLinksView({
  organizationName,
  onBack,
  load = fetchOrganizationLinks,
  propose = proposeOrganizationLink,
  accept = acceptOrganizationLink,
  update = updateOrganizationLink,
  end = endOrganizationLink,
}: OrganizationLinksViewProps) {
  const { t } = useLocale();
  const [result, setResult] = useState<LinksListResult | null>(null);
  const [generation, setGeneration] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  // ONE FRESH LIST PER OPEN, AND AFTER EVERY WRITE — the server's answer is
  // the only truth about what was agreed.
  useEffect(() => {
    let live = true;
    void load().then((r) => {
      if (live) setResult(r);
    });
    return () => {
      live = false;
    };
  }, [load, generation]);
  const reload = () => setGeneration((n) => n + 1);

  const list = result?.state === 'ok' ? result.list : null;
  const roles = list?.linkRoles ?? [];
  const roleById = new Map(roles.map((r) => [r.id, r] as const));

  /** What a role lets the other side do, in words. */
  const describe = (role: LinkRoleRef | null): string => {
    const option = role ? roleById.get(role.id) : undefined;
    const capabilities = option?.capabilities ?? [];
    if (!role || capabilities.length === 0) return t('links.capabilities.none');
    return capabilities.map((c) => t(`links.capability.${c}` as MessageKey)).join(' · ');
  };
  const rateText = (ore: number | null) =>
    ore === null ? t('links.rate.list') : t('links.rate.value', { amount: String(ore / 100) });

  const settle = async (outcome: LinkWriteResult, done: string) => {
    if (outcome.state === 'ok') {
      setNotice({ kind: 'ok', text: done });
      reload();
    } else if (outcome.state === 'forbidden') {
      setNotice({ kind: 'error', text: t('links.forbidden') });
    } else if (outcome.state === 'refused') {
      // THE CODE BECOMES A SENTENCE HERE and nowhere earlier.
      setNotice({ kind: 'error', text: errorMessage(t, outcome.reason) });
      reload();
    } else {
      setNotice({ kind: 'error', text: t('errors.unknown') });
    }
  };
  const run = async (action: () => Promise<LinkWriteResult>, done: string) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    await settle(await action(), done);
    setBusy(false);
  };

  const body = () => {
    if (result === null) {
      return (
        <p className="text-sm text-ink-3" data-testid="links-loading">
          {t('links.loading')}
        </p>
      );
    }
    if (result.state === 'forbidden') {
      return (
        <p className="text-sm text-danger" data-testid="links-forbidden">
          {t('links.forbidden')}
        </p>
      );
    }
    if (result.state !== 'ok') {
      return (
        <div data-testid="links-unavailable">
          <p className="text-sm text-danger">{t('links.unavailable')}</p>
          <button type="button" onClick={reload} className="btn-outline text-xs mt-2">
            {t('links.retry')}
          </button>
        </div>
      );
    }
    const isCentre = result.list.organization.kind === 'CENTRE';
    const { links } = result.list;
    return (
      <div className="space-y-4">
        <p
          className="flex items-start gap-2 rounded-lg border border-line bg-bg-soft px-3 py-2 text-xs text-ink-2"
          data-testid="links-in-use"
        >
          <Info size={14} className="mt-0.5 flex-shrink-0" /> {t('links.inUse')}
        </p>
        {notice && (
          <p
            role="status"
            data-testid="links-notice"
            className={`text-sm ${notice.kind === 'ok' ? 'text-ink-2' : 'text-danger'}`}
          >
            {notice.text}
          </p>
        )}

        {links.length === 0 ? (
          <p className="text-sm text-ink-3" data-testid="links-none">
            {isCentre ? t('links.none.centre') : t('links.none.club')}
          </p>
        ) : (
          <ul className="space-y-3">
            {links.map((link) => (
              <LinkCard
                key={link.id}
                link={link}
                roles={roles}
                busy={busy}
                describe={describe}
                rateText={rateText}
                onAccept={(gives) =>
                  run(() => accept(link.id, gives), t('links.accepted', { name: link.other.name }))
                }
                onSave={(change) => run(() => update(link.id, change), t('links.saved'))}
                onEnd={() => run(() => end(link.id), t('links.ended', { name: link.other.name }))}
                onInvalidRate={() => setNotice({ kind: 'error', text: t('links.rate.invalid') })}
                onInvalidWindow={() =>
                  setNotice({ kind: 'error', text: t('links.window.invalid') })
                }
              />
            ))}
          </ul>
        )}

        {isCentre && (
          <ProposeForm
            roles={roles}
            busy={busy}
            onPropose={(input) => run(() => propose(input), t('links.propose.done'))}
            onInvalidRate={() => setNotice({ kind: 'error', text: t('links.rate.invalid') })}
          />
        )}
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-10 slide-up" data-testid="links-view">
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        data-testid="links-back"
      >
        <ArrowLeft size={12} /> {t('links.back')}
      </button>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold mb-1">{t('links.title')}</h1>
        <p className="text-sm text-ink-3">
          {t(list?.organization.kind === 'CLUB' ? 'links.lead.club' : 'links.lead.centre', {
            organization: organizationName ?? t('links.thisOrganization'),
          })}
        </p>
      </div>
      {body()}
    </div>
  );
}

/** A LINK-role picker: «nothing», then every LINK role the server offers. */
function RoleSelect({
  roles,
  value,
  onChange,
  testId,
  disabled,
}: {
  roles: LinkRoleOption[];
  value: string;
  onChange: (id: string) => void;
  testId: string;
  disabled?: boolean;
}) {
  const { t } = useLocale();
  const roleName = useRoleName();
  return (
    <select
      className="input mt-1 w-full"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      disabled={disabled}
    >
      <option value={NONE}>{t('links.nothing')}</option>
      {roles.map((r) => (
        <option key={r.id} value={r.id}>
          {roleName(r)}
        </option>
      ))}
    </select>
  );
}

function LinkCard({
  link,
  roles,
  busy,
  describe,
  rateText,
  onAccept,
  onSave,
  onEnd,
  onInvalidRate,
  onInvalidWindow,
}: {
  link: OrganizationLinkView;
  roles: LinkRoleOption[];
  busy: boolean;
  describe: (role: LinkRoleRef | null) => string;
  rateText: (ore: number | null) => string;
  onAccept: (gives: string | null) => void;
  onSave: (change: {
    gives?: string | null;
    agreedCourtRateOre?: number | null;
    bookingWindowDays?: number | null;
  }) => void;
  onEnd: () => void;
  onInvalidRate: () => void;
  onInvalidWindow: () => void;
}) {
  const { t } = useLocale();
  const roleName = useRoleName();
  const awaitingUs = link.status === 'proposed' && link.side === 'club';
  const awaitingThem = link.status === 'proposed' && link.side === 'centre';
  // What this side gives, as a draft until saved. A club answering a proposal
  // starts from Starpoint's default for its side, when there is one.
  const defaultGive = awaitingUs
    ? (roles.find((r) => r.key === 'LINK_HOST_CENTRE')?.id ?? NONE)
    : NONE;
  const [gives, setGives] = useState(link.gives?.id ?? defaultGive);
  const [rate, setRate] = useState(
    link.agreedCourtRateOre === null ? '' : String(link.agreedCourtRateOre / 100),
  );
  // HOW FAR AHEAD THE PARTNER CLUB MAY BOOK (slice 4c, 4A): the centre's term.
  const [windowText, setWindowText] = useState(
    link.bookingWindowDays === null ? '' : String(link.bookingWindowDays),
  );
  const statusKey = awaitingUs
    ? 'links.status.proposedToYou'
    : (`links.status.${link.status}` as MessageKey);
  const givesChanged = gives !== (link.gives?.id ?? NONE);
  const rateChanged =
    link.side === 'centre' &&
    rate.trim() !== (link.agreedCourtRateOre === null ? '' : String(link.agreedCourtRateOre / 100));
  const windowChanged =
    link.side === 'centre' &&
    windowText.trim() !== (link.bookingWindowDays === null ? '' : String(link.bookingWindowDays));

  const save = () => {
    const change: {
      gives?: string | null;
      agreedCourtRateOre?: number | null;
      bookingWindowDays?: number | null;
    } = {};
    if (givesChanged) change.gives = gives === NONE ? null : gives;
    if (rateChanged) {
      const parsed = parseRate(rate);
      if (parsed === undefined) return onInvalidRate();
      change.agreedCourtRateOre = parsed;
    }
    if (windowChanged) {
      const days = parseWindow(windowText);
      if (days === undefined) return onInvalidWindow();
      change.bookingWindowDays = days;
    }
    onSave(change);
  };

  return (
    <li className="card p-5" data-testid={`link-${link.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
          <Handshake size={18} /> {link.other.name}
        </h2>
        <span
          className="rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-3"
          data-testid={`link-status-${link.id}`}
        >
          {t(statusKey)}
        </span>
      </div>

      <dl className="mt-3 grid gap-3 sm:grid-cols-2 text-sm">
        <div>
          <dt className="font-mono uppercase tracking-wider text-xs text-ink-3">
            {t('links.receives')}
          </dt>
          <dd className="mt-1 text-ink" data-testid={`link-receives-${link.id}`}>
            {awaitingThem ? (
              <span className="text-ink-3">{t('links.notAnswered')}</span>
            ) : (
              <>
                <span className="font-medium">
                  {link.receives ? roleName(link.receives) : t('links.nothing')}
                </span>
                <span className="block text-xs text-ink-3">{describe(link.receives)}</span>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt className="font-mono uppercase tracking-wider text-xs text-ink-3">
            {awaitingUs ? t('links.accept.choose') : t('links.gives')}
          </dt>
          <dd className="mt-1">
            <RoleSelect
              roles={roles}
              value={gives}
              onChange={setGives}
              testId={`link-gives-${link.id}`}
              disabled={busy}
            />
            <span className="block mt-1 text-xs text-ink-3">
              {describe(gives === NONE ? null : (roles.find((r) => r.id === gives) ?? null))}
            </span>
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="font-mono uppercase tracking-wider text-xs text-ink-3">
            {t('links.rate')}
          </dt>
          <dd className="mt-1 text-ink" data-testid={`link-rate-${link.id}`}>
            {link.side === 'centre' ? (
              <input
                className="input w-full sm:w-48"
                inputMode="numeric"
                value={rate}
                placeholder={t('links.rate.list')}
                aria-label={t('links.rate.input')}
                onChange={(e) => setRate(e.target.value)}
                data-testid={`link-rate-input-${link.id}`}
                disabled={busy}
              />
            ) : (
              rateText(link.agreedCourtRateOre)
            )}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="font-mono uppercase tracking-wider text-xs text-ink-3">
            {t('links.window')}
          </dt>
          <dd className="mt-1 text-ink" data-testid={`link-window-${link.id}`}>
            {link.side === 'centre' ? (
              <input
                className="input w-full sm:w-48"
                inputMode="numeric"
                value={windowText}
                placeholder={t('links.window.ordinary')}
                aria-label={t('links.window.input')}
                onChange={(e) => setWindowText(e.target.value)}
                data-testid={`link-window-input-${link.id}`}
                disabled={busy}
              />
            ) : link.bookingWindowDays === null ? (
              t('links.window.ordinary')
            ) : (
              t('links.window.value', { days: String(link.bookingWindowDays) })
            )}
          </dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        {awaitingUs ? (
          <>
            <ConfirmButton
              question={t('links.decline.question', { name: link.other.name })}
              confirmLabel={t('links.decline')}
              onConfirm={onEnd}
              className="btn-outline text-xs"
              disabled={busy}
            >
              {t('links.decline')}
            </ConfirmButton>
            <button
              type="button"
              className="btn-lime text-xs"
              onClick={() => onAccept(gives === NONE ? null : gives)}
              disabled={busy}
              data-testid={`link-accept-${link.id}`}
            >
              {t('links.accept')}
            </button>
          </>
        ) : (
          <>
            <ConfirmButton
              question={t(awaitingThem ? 'links.withdraw.question' : 'links.end.question', {
                name: link.other.name,
              })}
              confirmLabel={t(awaitingThem ? 'links.withdraw' : 'links.end')}
              onConfirm={onEnd}
              className="btn-outline text-xs"
              disabled={busy}
            >
              {t(awaitingThem ? 'links.withdraw' : 'links.end')}
            </ConfirmButton>
            <button
              type="button"
              className="btn-lime text-xs"
              onClick={save}
              disabled={busy || (!givesChanged && !rateChanged && !windowChanged)}
              data-testid={`link-save-${link.id}`}
            >
              {busy ? t('links.saving') : t('links.save')}
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function ProposeForm({
  roles,
  busy,
  onPropose,
  onInvalidRate,
}: {
  roles: LinkRoleOption[];
  busy: boolean;
  onPropose: (input: {
    clubSlug: string;
    gives: string | null;
    agreedCourtRateOre: number | null;
  }) => void;
  onInvalidRate: () => void;
}) {
  const { t } = useLocale();
  const [clubSlug, setClubSlug] = useState('');
  // Starpoint's default for what a centre gives, when it exists.
  const [gives, setGives] = useState(roles.find((r) => r.key === 'LINK_PARTNER_CLUB')?.id ?? NONE);
  const [rate, setRate] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!clubSlug || busy) return;
    const parsed = parseRate(rate);
    if (parsed === undefined) return onInvalidRate();
    onPropose({ clubSlug, gives: gives === NONE ? null : gives, agreedCourtRateOre: parsed });
  };

  return (
    <section className="card p-5" data-testid="links-propose">
      <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
        <Send size={18} /> {t('links.propose.title')}
      </h2>
      <p className="mt-1 text-xs text-ink-3">{t('links.propose.lead')}</p>
      <form onSubmit={submit} className="mt-3 grid gap-3">
        <label className="block">
          <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
            {t('links.propose.club')}
          </span>
          <div className="mt-1">
            <ClubPicker value={clubSlug} onChange={setClubSlug} disabled={busy} />
          </div>
        </label>
        <label className="block">
          <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
            {t('links.propose.gives')}
          </span>
          <RoleSelect
            roles={roles}
            value={gives}
            onChange={setGives}
            testId="links-propose-gives"
          />
        </label>
        <label className="block">
          <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
            {t('links.rate.input')}
          </span>
          <input
            className="input mt-1 w-full sm:w-48"
            inputMode="numeric"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            data-testid="links-propose-rate"
          />
        </label>
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-lime text-xs"
            disabled={busy || !clubSlug}
            data-testid="links-propose-submit"
          >
            {t('links.propose.submit')}
          </button>
        </div>
      </form>
    </section>
  );
}
